/**
 * Показ посева в ЗАВЕРШЁННОМ сезоне (канон терминологии сезона v3 §5).
 *
 * Проблема: сезон 2019 давно прошёл, а занятие в нём так и лежит со статусом
 * `planned` — интерфейс пишет «Запланировано». Для агронома это враньё: ничего
 * уже не будет запланировано, сезон закончился. Такие строки — это либо сев,
 * который не отметили работами, либо план, который так и не выполнили.
 *
 * Здесь — ТОЛЬКО подмена ПОКАЗА. Данные не трогаем: `sowings.status` двигает
 * автомат от тех.операций (ADR-063), и массовая конвертация `planned` →
 * `growing`/`harvested` в истории — отдельное решение founder (правило
 * конвертации + dry-run отчёт `backend/scripts/report_planned_sowings_closed_seasons.py`).
 * Пока сервер отдаёт `planned`, мы честно говорим «Не реализован» и объясняем
 * почему — а не выдумываем факт сева за агронома.
 *
 * Подменяется единственный статус — «Запланировано». Всё остальное
 * («Растёт», «Убрано», «Списано», «Не запланировано», «Пар») в завершённом
 * сезоне читается нормально и остаётся как есть.
 */
import { getSowingStatusInfo, type SowingStatusInfo } from '@/components/fields/sowingStatus';
import { sowingStage, type SowingStageSource } from '@/lib/sowings/stage';

/** Слово вместо «Запланировано» у посева завершённого сезона. */
export const UNREALIZED_SOWING_LABEL = 'Не реализован';

/** Объяснение под курсором — почему статус читается так. */
export const UNREALIZED_SOWING_HINT = 'Сезон завершён, факт сева не зафиксирован';

/** Приглушённый тон: строка есть, но событием она не стала. */
const UNREALIZED_SOWING_CLASS = 'bg-muted text-muted-foreground';

export interface SowingDisplay extends SowingStatusInfo {
  /** Текст всплывающей подсказки (native `title`) либо null. */
  hint: string | null;
  /** Показ подменён правилом завершённого сезона (данные не менялись). */
  isUnrealized: boolean;
}

/**
 * Как показать статус посева с учётом того, завершён ли его сезон.
 *
 * @param status  разрешённый код статуса (`crop_status`) или сырой `sowings.status`
 * @param seasonCompleted сезон посева завершён (см. `isSeasonCompleted`)
 */
export function resolveSowingDisplay(
  status: string | null | undefined,
  seasonCompleted?: boolean | null,
): SowingDisplay | null {
  const info = getSowingStatusInfo(status);
  if (!info) return null;
  if (seasonCompleted && status === 'planned') {
    return {
      label: UNREALIZED_SOWING_LABEL,
      className: UNREALIZED_SOWING_CLASS,
      hint: UNREALIZED_SOWING_HINT,
      isUnrealized: true,
    };
  }
  return { ...info, hint: null, isUnrealized: false };
}

/**
 * То же для занятия целиком: статус берётся через `sowingStage()` (тот же
 * порядок источников, что у полосы прогресса и попапа карты).
 */
export function resolveSowingDisplayFor(
  sowing: SowingStageSource | null | undefined,
  seasonCompleted?: boolean | null,
): SowingDisplay | null {
  return resolveSowingDisplay(sowingStage(sowing), seasonCompleted);
}
